<template>
  <tr class="row-data">
    <td
      @click="$emit('clickOnDateCell', date)"
      class="day-data sticky-columns-shadow"
      :class="[color_class, { weekend: isWeekend }]"
    >
      <div class="cell" v-if="!isSummary">
        <div style="gap: 3px" class="d-flex flex-column w-100">
          <div class="d-flex justify-start pl-2">
            <span v-html="day_of_week"></span>
            <v-tooltip v-if="is_today" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  color="secondary"
                  size="14"
                  class="ml-1"
                >
                  mdi-calendar-today
                </v-icon>
              </template>
              <span class="text-uppercase">{{ $t("today") }}</span>
            </v-tooltip>
            <v-spacer></v-spacer>
            <a-rms-strategy-day-events
              v-for="impact in impacts"
              :key="impact.impact"
              :impact="impact"
              :events="events"
              :date="date"
            />
          </div>
          <div class="d-flex justify-start pl-2 grey--text">
            <span v-html="day_of_week_vs"></span>
            <v-spacer></v-spacer>
            <a-rms-strategy-day-events
              v-for="impact_vs in impacts_vs"
              :key="impact_vs.impact"
              :impact="impact_vs"
              :events="events_vs"
              :date="date_vs"
            />
          </div>
        </div>
      </div>
      <div class="cell justify-start" v-else>
        <div class="d-flex justify-start pl-2">
          <span>{{ $t("result_period") }}</span>
        </div>
      </div>
    </td>
    <td
      v-for="column in nonStickyColumnHeaders"
      :key="`${date}-${column.group}-${column.column}`"
      :class="[
        {
          'sticky-columns-shadow': column.column === lastStickyColumn.column
        },
        { weekend: isWeekend },
        getClass(
          configStrategyTable[column.group].columns[column.column].kpi,
          getValue(column),
          column
        ),
        { 'br-bolder': column.column === lastColumnOfEachGroup[column.group] },
        { previous: isPrevious },
        { summary: isSummary }
      ]"
      @mouseover="$emit('hoveredKpi', column)"
      @mouseleave="$emit('hoveredKpi', '')"
    >
      <div class="position-relative h-100">
        <div class="cell pr-1">
          <template v-if="column.group.startsWith('pickup_')">
            <template v-if="rms.strategy_vs_spot === 'stly'">
              <div class="d-flex flex-column align-end" style="gap: 3px">
                <a-kpi
                  :kpi="
                    configStrategyTable[column.group].columns[column.column].kpi
                  "
                  :value="getStaticKpiValue(column)"
                  x_small
                  absolute
                />
                <a-kpi
                  :kpi="
                    configStrategyTable[column.group].columns[column.column].kpi
                  "
                  :value="getStaticKpiValue(column)"
                  :vs_values="[
                    getStaticKpiValue(
                      column,
                      `pickup_stly_${column.group.slice(-1)}`
                    )
                  ]"
                  :vs_periods="[rms.strategy_vs_spot]"
                  xx_small
                  hide_kpi
                  absolute
                />
              </div>
            </template>
            <template v-else>
              <div class="d-flex flex-column align-end" style="gap: 3px">
                <a-kpi
                  :kpi="
                    configStrategyTable[column.group].columns[column.column].kpi
                  "
                  :value="getStaticKpiValue(column)"
                  x_small
                  absolute
                />
              </div>
            </template>
          </template>
          <template v-else>
            <!-- <a-kpi
              :kpi="
                configStrategyTable[column.group].columns[column.column].kpi
              "
              :value="getValue(column)"
              :vs_values="[getValue(column, rms.strategy_vs_spot)]"
              :vs_periods="[rms.strategy_vs_spot]"
              :class="{ 'pt-4': !getValue(column) }"
              x_small
              absolute
              vertical
            /> -->
            <div class="d-flex flex-column align-end" style="gap: 3px">
              <a-kpi
                :kpi="
                  configStrategyTable[column.group].columns[column.column].kpi
                "
                :value="getValue(column)"
                x_small
                absolute
              />
              <a-kpi
                :kpi="
                  configStrategyTable[column.group].columns[column.column].kpi
                "
                :value="getValue(column)"
                :vs_values="[getValue(column, rms.strategy_vs_spot)]"
                :vs_periods="[rms.strategy_vs_spot]"
                xx_small
                absolute
                hide_kpi
              />
            </div>
          </template>
        </div>
        <div
          v-if="
            showProgressPercent(
              configStrategyTable[column.group].columns[column.column].kpi
            )
          "
          class="percent-progress"
          :style="getPercentStyle(getValue(column))"
        ></div>
      </div>
    </td>
  </tr>
</template>
<script>
import aRmsStrategyDayEvents from "@/components/rms/aRmsStrategyDayEvents";
import aKpi from "@/components/rms_ui/aKpi.vue";

import { getFriendlyDayStrategy } from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";

export default {
  name: "a-rms-strategy-day-table-row",
  emits: ["clickOnDateCell", "hoveredKpi"],
  components: { aKpi, aRmsStrategyDayEvents },
  props: {
    row: Object,
    date: String,
    nonStickyColumnHeaders: Array,
    lastStickyColumn: Object,
    configStrategyTable: Object,
    lastColumnOfEachGroup: Object,
    isSummary: Boolean
  },
  methods: {
    showProgressPercent(kpi) {
      let type = this.get_kpi(kpi).data_format;
      if (type === "percent1" || type === "percent2") {
        return true;
      } else {
        return false;
      }
    },
    getPercentStyle(value) {
      return "width:" + value + "%;";
    },
    getClass(kpi, value, column) {
      // If type of kpi is percent we must add a class to the cell named  kpi-percent-range-0-20, kpi-percent-range-20-40, kpi-percent-range-40-60, kpi-percent-range-60-80, kpi-percent-range-80-100
      let type = this.get_kpi(kpi).data_format;
      if (value === null) return "";
      if (type === "percent1" || type === "percent2") {
        if (value < 20) {
          return "kpi-percent-range-0-20";
        } else if (value >= 20 && value < 40) {
          return "kpi-percent-range-20-40";
        } else if (value >= 40 && value < 60) {
          return "kpi-percent-range-40-60";
        } else if (value >= 60 && value < 80) {
          return "kpi-percent-range-60-80";
        } else if (value >= 80) {
          return "kpi-percent-range-80-100";
        }
      } else if (column.group === "roomtype") {
        return !this.isSummary &&
          value > this.configStrategyTable.roomtype.columns[column.column].limit
          ? "limit"
          : "";
      } else if (column.group.startsWith("pickup_")) {
        return value <=
          this.configStrategyTable[column.group].columns[column.column].limit
          ? "limit"
          : "";
      } else if (
        column.group === "inventory" &&
        (column.column === "available" || column.column === "available_pace")
      ) {
        return !this.isSummary &&
          value <=
            this.configStrategyTable[column.group].columns[column.column].limit
          ? "limit"
          : "";
      } else {
        return "";
      }
    }
  },
  computed: {
    isWeekend() {
      return !this.isSummary && Vue.moment(this.date).isoWeekday() > 4;
    },
    isPrevious() {
      return (
        !this.isSummary &&
        Vue.moment(this.date).isBefore(
          Vue.moment()
            .endOf("day")
            .add(-1, "days")
        )
      );
    },
    getStaticKpiValue() {
      return function(column, vs = undefined) {
        const spotIndex = vs
          ? vs
          : this.configStrategyTable[column.group] &&
            this.configStrategyTable[column.group].columns[column.column].block;
        return this.row &&
          this.row[spotIndex] &&
          this.configStrategyTable[column.group] &&
          this.row[spotIndex][
            this.configStrategyTable[column.group].columns[column.column].kpi
          ]
          ? this.row[spotIndex][
              this.configStrategyTable[column.group].columns[column.column].kpi
            ]
          : null;
      };
    },
    getDynamicKpiValue() {
      return function(column, dynamicKey = "roomtype", vs = undefined) {
        const spotIndex = vs
          ? vs
          : this.configStrategyTable[column.group].columns[column.column].block;

        return this.row &&
          this.row[spotIndex] &&
          this.row[spotIndex][dynamicKey] &&
          this.row[spotIndex][dynamicKey][column.column] &&
          this.row[spotIndex][dynamicKey][column.column][
            this.configStrategyTable[column.group].columns[column.column].kpi
          ]
          ? this.row[spotIndex][dynamicKey][column.column][
              this.configStrategyTable[column.group].columns[column.column].kpi
            ]
          : null;
      };
    },
    getValue() {
      return function(column, vs = undefined) {
        if (column.group === "roomtype") {
          return this.getDynamicKpiValue(column, "roomtype", vs);
        } else if (column.group === "segment") {
          return this.getDynamicKpiValue(column, "segment", vs);
        } else if (column.group === "cityMKD") {
          return column.column === "MKD-Final" || vs
            ? this.getStaticKpiValue(column, vs)
            : this.getStaticKpiValue(
                { column: "MKD-Final", group: "cityMKD" },
                vs
              ) - this.getStaticKpiValue(column, vs);
        } else if (column.column === "room_revenue_other") {
          return (
            this.getStaticKpiValue(
              { column: "room_revenue_total", group: "revenue" },
              vs
            ) -
            this.getStaticKpiValue(
              { column: "room_revenue", group: "revenue" },
              vs
            )
          );
        } else {
          return this.getStaticKpiValue(column, vs);
        }
      };
    },
    color_class() {
      if (!this.row || !this.row.otb || !this.row.bud) return "neutral";

      let diff = this.row.otb.occupancy - this.row.bud.occupancy;
      if (diff > 0) return "positive";
      if (diff < 0) return "negative";
      return "neutral";
    },
    day_of_week() {
      return getFriendlyDayStrategy(this.date, true);
    },
    day_of_week_vs() {
      return getFriendlyDayStrategy(this.date_vs, true);
    },
    is_today() {
      return this.date === Vue.moment().format("YYYY-MM-DD");
    },
    events() {
      if (
        this.calendar_store.hotel_comparative_dates &&
        this.calendar_store.hotel_comparative_dates[this.date] &&
        this.calendar_store.hotel_comparative_dates[this.date].events
      ) {
        return this.calendar_store.hotel_comparative_dates[this.date].events;
      }
      return [];
    },
    events_vs() {
      if (
        this.date_vs &&
        this.calendar_store.hotel_comparative_dates &&
        this.calendar_store.hotel_comparative_dates[this.date_vs] &&
        this.calendar_store.hotel_comparative_dates[this.date_vs].events
      ) {
        return this.calendar_store.hotel_comparative_dates[this.date_vs].events;
      }
      return [];
    },
    impacts() {
      let events_count = this.calendar_store.impacts.map(i => {
        return {
          impact: i.value,
          color: i.color,
          count: this.events.filter(e => e.impact === i.value).length
        };
      });
      return events_count.filter(e => e.count > 0);
    },
    impacts_vs() {
      let events_count = this.calendar_store.impacts.map(i => {
        return {
          impact: i.value,
          count: this.events_vs.filter(e => e.impact === i.value).length
        };
      });
      return events_count.filter(e => e.count > 0);
    },
    date_vs() {
      let date_vs = "";

      // Para el budget
      if (this.rms.strategy_vs_spot === "bud") {
        return Vue.moment(this.date);
      }

      // Si es LY o STLY, usar la misma fecha del año anterior (cálculo directo)
      if (
        this.rms.strategy_vs_spot === "lyn" ||
        this.rms.strategy_vs_spot === "stlyn"
      ) {
        return Vue.moment(this.date)
          .subtract(1, "year")
          .format("YYYY-MM-DD");
      }

      // Si es STLYN o LYN (o por defecto), usar date_vs del store
      let items = this.calendar_store.hotel_comparative_dates;
      if (Object.keys(items).length > 0 && items.hasOwnProperty(this.date)) {
        date_vs = items[this.date].date_vs;
      }
      return date_vs;
    },
    ...mapState({
      rms: state => state.rms,
      calendar_store: state => state.calendar
    }),
    ...mapGetters({
      current_hotel: "current_hotel",
      get_kpi: "get_kpi"
    })
  }
};
</script>

<style lang="scss" scoped>
tr {
  td {
    border: none;
    background-color: white;
    border-left: $borderType $borderColorV;
    border-bottom: $borderType $borderColorV;
    &:nth-child(1) {
      /* border-right: 2px solid $borderColorV; */
      box-shadow: inset -2px 0 0 0 #ccc;
      position: sticky;
      left: 0;
      background: white;
      color: #e0e0e0;
      font-weight: normal;
      z-index: 2;
      padding: 3px 5px;
    }
    &.sticky-columns-shadow {
      box-shadow: 1px 0px 0px 0px rgba(0, 0, 0, 0.15),
        2px 0px 0px 0px rgba(0, 0, 0, 0.12), 4px 0px 0px 0px rgba(0, 0, 0, 0.08);
    }
    &:hover {
      background-color: #f5fdff !important;
    }
    &.kpi-percent-range-0-20 {
      background-color: rgb(240, 218, 205) !important;
      &.summary {
        background-color: #f0d9cc !important;
      }
      .percent-progress {
        background-color: rgba(181, 68, 3, 1) !important;
      }
    }
    &.kpi-percent-range-20-40 {
      background-color: rgb(243, 230, 204) !important;
      &.summary {
        background-color: #f7e6cc !important;
      }
      .percent-progress {
        background-color: rgba(219, 131, 0, 1) !important;
      }
    }
    &.kpi-percent-range-40-60 {
      background-color: rgb(249, 239, 204) !important;
      &.summary {
        background-color: #fdf1cc !important;
      }
      .percent-progress {
        background-color: rgba(246, 185, 0, 1) !important;
      }
    }
    &.kpi-percent-range-60-80 {
      background-color: rgb(243, 239, 205) !important;
      &.summary {
        background-color: #ebe8cc !important;
      }
      .percent-progress {
        background-color: rgba(158, 143, 2, 1) !important;
      }
    }
    &.kpi-percent-range-80-100 {
      background-color: rgb(223, 239, 205) !important;
      &.summary {
        background-color: #dbe6cc !important;
      }
      .percent-progress {
        background-color: rgba(79, 131, 2, 1) !important;
      }
    }
    &.day-data {
      font-size: 0.8rem;
      font-weight: 700;
      color: #555;
      text-transform: uppercase;
      cursor: pointer;
      &::after {
        content: " ";
        position: absolute;
        top: 0;
        left: 3px;
        height: calc(100% - 8px);
        width: 4px;
        background-color: grey;
        z-index: 10;
        border-radius: 5px;
        margin: 4px 0;
      }
      &.positive::after {
        background-color: rgb(23, 171, 112);
      }
      &.negative::after {
        background-color: rgb(172, 0, 0);
      }
      &.neutral::after {
        background-color: orange;
      }
    }
    &.weekend {
      background-color: #fffef4;
    }
    &.previous {
      opacity: 1;
      background-color: #e5e5e5;
      &:hover {
        opacity: 1;
      }
    }
    &.limit {
      color: red !important;
    }
    .percent-progress {
      content: "";
      position: absolute;
      bottom: -5px;
      height: 2px;
      border-radius: 5px;
    }
    .cell {
      line-height: 16px;
      font-size: 12px;
      text-align: center;
      white-space: nowrap;
      min-width: 100%;
      text-wrap: wrap;
      text-transform: uppercase;
      justify-content: center;
      align-items: center;
      &.kpi-row {
        border-right: $borderType $borderColorV;
        border-bottom: 2px solid transparent;
      }
      &.kpi-hovered {
        border-bottom-color: #2d6ece;
      }
      ::v-deep .kpi-wrapper-2.vertical {
        .a-kpi {
          margin: 0 !important;
        }
      }
    }
  }

  &:hover {
    td.sticky-columns-shadow {
      /* border-right-color: #2d6ece !important; */
      box-shadow: inset -2px 0 0 0 #2d6ece;
    }
  }

  &.summary {
    box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0.15),
      0px -2px 0px 0px rgba(0, 0, 0, 0.12), 0px -4px 0px 0px rgba(0, 0, 0, 0.08);
    position: sticky;
    bottom: 0;
    z-index: 2;
    td {
      background-color: #ebebeb;
      .cell {
        padding: 5px 0 2px;
      }
      .percent-progress {
        bottom: -1px;
      }
    }
  }
}
</style>
